"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.findIntersections = findIntersections;
exports.removeCachedEntityBboxs = removeCachedEntityBboxs;
var _BoundingBoxIntersector = require("./BoundingBoxIntersector");
var _computeBbox = require("./computeBbox");
var _intersectEntities = require("./intersectEntities");
const symBbox = Symbol('bbox');

/**
 * Finds points of intersection between two contours.
 *
 * As part of this process, each entity's bounding box is pre-computed, if it does not already exist, and deleted before
 * the function returns. To improve performance in cases where this function will be run multiple times on the same
 * contours, you can pass `false` as the third argument to keep the cached bounding boxes instead of deleting them.
 *
 * @param contour The first contour, containing an array of entities
 * @param contour The second contour, containing an array of entities
 * @param autoRemoveCachedBbox Whether to remove the cached bounding boxes of the entities before
 * returning. The default is true.
 */
function findIntersections(contour1, contour2, autoRemoveCachedBbox = true) {
  // Precompute bounding boxes on each entity
  for (let e of contour1.entities) {
    if (!e[symBbox]) e[symBbox] = (0, _computeBbox.computeBbox)([e]);
  }
  for (let e of contour2.entities) {
    if (!e[symBbox]) e[symBbox] = (0, _computeBbox.computeBbox)([e]);
  }
  const intersectingPoints = [];
  let intersector = new _BoundingBoxIntersector.BoundingBoxIntersectorGrouped([contour1.entities, contour2.entities], e => (0, _computeBbox.computeBbox)([e]), {
    ignoreAllCollisionsWithinGroup: true
  });
  for (let intersection of intersector.getIntersections()) {
    let entity1;
    let entity2;
    if (intersection[0].groupIndex === 0 && intersection[1].groupIndex === 1) {
      entity1 = intersection[0].item;
      entity2 = intersection[1].item;
    } else if (intersection[0].groupIndex === 1 && intersection[1].groupIndex === 0) {
      entity1 = intersection[1].item;
      entity2 = intersection[0].item;
    } else {
      throw new Error('Unexpected group index');
    }

    // Check to see if the entities actually intersect
    intersectingPoints.push(...(0, _intersectEntities.intersectEntities)(entity1, entity2).map(({
      point,
      t1,
      t2
    }) => ({
      point,
      t1,
      t2,
      entity1,
      entity2
    })));
  }

  // The caller can choose to retain the bounding boxes, to be used in a subsequent call to this function.
  if (autoRemoveCachedBbox) {
    removeCachedEntityBboxs(contour1);
    removeCachedEntityBboxs(contour2);
  }
  return intersectingPoints;
}

/**
 * Removes the cached entity bounding boxes from the given contour, which were created during `findIntersections`.
 * @param {Contour} contour The contour whose entities should have their bounding boxes removed.
 */
function removeCachedEntityBboxs(contour) {
  for (let e of contour.entities) {
    delete e[symBbox];
  }
}